import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OutletProvider } from './contexts/OutletContext';
import { ShiftProvider } from './contexts/ShiftContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { QZPrintProvider } from './contexts/QZPrintContext';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import POS from './pages/POS';
import Orders from './pages/Orders';
import Kitchen from './pages/Kitchen';
import Tables from './pages/Tables';
import Customers from './pages/Customers';
import Menu from './pages/Menu';
import Inventory from './pages/Inventory';
import Reports from './pages/Reports';
import UsersPage from './pages/Users';
import Settings from './pages/Settings';
import Shifts from './pages/Shifts';
import VoidLogs from './pages/VoidLogs';
import Help from './pages/Help';
import CustomerOrder from './pages/CustomerOrder';
import Printers from './pages/Printers';
import { Loader2 } from 'lucide-react';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
      
      {/* Public route for QR ordering - no auth required */}
      <Route path="/order" element={<CustomerOrder />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER']}>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/pos" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER', 'CASHIER']}>
          <POS />
        </ProtectedRoute>
      } />
      
      <Route path="/orders" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER', 'CASHIER']}>
          <Orders />
        </ProtectedRoute>
      } />
      
      <Route path="/kitchen" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER', 'KITCHEN']}>
          <Kitchen />
        </ProtectedRoute>
      } />
      
      <Route path="/tables" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER', 'CASHIER']}>
          <Tables />
        </ProtectedRoute>
      } />
      
      <Route path="/customers" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER', 'CASHIER']}>
          <Customers />
        </ProtectedRoute>
      } />
      
      <Route path="/menu" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER']}>
          <Menu />
        </ProtectedRoute>
      } />
      
      <Route path="/inventory" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER']}>
          <Inventory />
        </ProtectedRoute>
      } />
      
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER']}>
          <Reports />
        </ProtectedRoute>
      } />
      
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER']}>
          <UsersPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN']}>
          <Settings />
        </ProtectedRoute>
      } />
      
      <Route path="/shifts" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER']}>
          <Shifts />
        </ProtectedRoute>
      } />
      
      <Route path="/printers" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER', 'CASHIER']}>
          <Printers />
        </ProtectedRoute>
      } />
      
      <Route path="/void-logs" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER']}>
          <VoidLogs />
        </ProtectedRoute>
      } />
      
      <Route path="/help" element={
        <ProtectedRoute allowedRoles={['OWNER', 'ADMIN', 'MANAGER', 'CASHIER', 'KITCHEN']}>
          <Help />
        </ProtectedRoute>
      } />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OutletProvider>
          <ShiftProvider>
            <CurrencyProvider>
              <QZPrintProvider>
                <AppRoutes />
                <Toaster richColors position="top-right" />
              </QZPrintProvider>
            </CurrencyProvider>
          </ShiftProvider>
        </OutletProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
